import React, { useState, useEffect } from 'react';
import { FiPhone, FiMail } from 'react-icons/fi';
import '../styles/ContactModal.css';
import analyticsService from '../services/analyticsService';
import { useModalClose } from '../hooks/useModalClose';
import { formatPhoneNumber } from '../utils/phoneUtils';
import { getMemberByKeySync, getMemberByKeyAsync } from '../services/membersService';

const AgentContactModal = ({ show, onClose, memberKey, className }) => {
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Smooth close handler with animation - match photo modal timing (0.2s)
  const { isClosing, handleClose } = useModalClose(onClose, 200);

  // Preload the balloon logo image
  useEffect(() => {
    const preloadImage = () => {
      const img = new Image();
      img.src = '/images/rmxballoonz.png';
    };
    preloadImage();
  }, []);

  // Fetch agent data when modal opens
  useEffect(() => {
    if (show && memberKey) {
      setIsInitialLoad(true);
      setError(null);
      
      // Try synchronous lookup first (instant if cached)
      const cachedAgent = getMemberByKeySync(memberKey);
      if (cachedAgent) {
        setAgentData(cachedAgent);
        setLoading(false);
        // Remove artificial delay - show immediately
        setIsInitialLoad(false);
      } else {
        // Fallback to async if not cached yet
        fetchAgentData();
      }
    } else if (!show) {
      // Reset states when modal closes
      setAgentData(null);
      setError(null);
      setIsInitialLoad(true);
    }
  }, [show, memberKey]);

  // Adjust font size for long email addresses
  useEffect(() => {
    if (agentData?.email && show) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const emailLink = document.querySelector('.contact-info-item a[href^="mailto:"]');
        if (emailLink) {
          const emailText = agentData.email;
          const containerWidth = emailLink.parentElement.offsetWidth;
          const iconWidth = 30; // Approximate icon width + margin
          const availableWidth = containerWidth - iconWidth;
          
          // Create a temporary element to measure text width
          const tempElement = document.createElement('span');
          tempElement.style.visibility = 'hidden';
          tempElement.style.position = 'absolute';
          tempElement.style.fontSize = '1rem';
          tempElement.style.fontFamily = getComputedStyle(emailLink).fontFamily;
          tempElement.textContent = emailText;
          document.body.appendChild(tempElement);
          
          const textWidth = tempElement.offsetWidth;
          document.body.removeChild(tempElement);
          
          // Remove existing classes
          emailLink.classList.remove('long-email', 'very-long-email');
          
          // Add appropriate class based on text length
          if (textWidth > availableWidth * 1.2) {
            emailLink.classList.add('very-long-email');
          } else if (textWidth > availableWidth) {
            emailLink.classList.add('long-email');
          }
        }
      }, 100);
    }
  }, [agentData?.email, show]);

  const fetchAgentData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use the members service (will fetch if not cached)
      const agentData = await getMemberByKeyAsync(memberKey);

      if (agentData) {
        setAgentData(agentData);
      } else {
        throw new Error('Agent not found');
      }
    } catch (err) {
      console.error('Error fetching agent data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      // Remove artificial delay - show immediately when data is ready
      setIsInitialLoad(false);
    }
  };

  if (!show) return null;

  return (
    <div className={`modal-overlay ${className || ''} ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-button" onClick={handleClose} aria-label="Close agent contact modal">
          ×
        </button>
        
        <img
          src="/images/rmxballoonz.png"
          width="50"
          height="50"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          alt="Balloon Logo"
          className="modal-balloon-logo"
        />
        
        <div className="modal-contact-header">
          <h3 className="modal-agent-name" style={{ opacity: isInitialLoad ? 0 : 1, transition: 'opacity 0.2s ease' }}>
            {agentData?.name || 'Agent'}
          </h3>
          <p className="modal-agent-company" style={{ opacity: isInitialLoad ? 0 : 1, transition: 'opacity 0.2s ease' }}>
            {agentData?.office || 'REMAX Key Properties'}
          </p>
        </div>

        {error && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Error: {error}</p>
            <button onClick={handleClose} style={{ marginTop: '10px', padding: '8px 16px' }}>
              Close
            </button>
          </div>
        )}

        {agentData && !error ? (
          <div style={{ opacity: isInitialLoad ? 0 : 1, transition: 'opacity 0.2s ease' }}>
            {agentData.phone && (
              <p className="contact-info-item">
                <FiPhone className="contact-icon" />
                <a 
                  href={`tel:${agentData.phone}`}
                  onClick={() => {
                    analyticsService.trackContact('Phone Call', agentData.name || 'Unknown Agent', {
                      source: 'Agent Contact Modal'
                    });
                  }}
                >
                  {formatPhoneNumber(agentData.phone)}
                </a>
              </p>
            )}
            {agentData.email && (
              <p className="contact-info-item">
                <FiMail className="contact-icon" />
                <a 
                  href={`mailto:${agentData.email}`}
                  onClick={() => {
                    analyticsService.trackContact('Email', agentData.name || 'Unknown Agent', {
                      source: 'Agent Contact Modal'
                    });
                  }}
                >
                  {agentData.email}
                </a>
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AgentContactModal;